/**
 * WebSocketTransport
 *
 * A deterministic WebSocket state machine, modeled directly on SIP.js's
 * `Transport` (src/platform/web/transport/transport.ts). It intentionally
 * contains NO reconnection, token, or liveness logic — those are policy and
 * live one layer up in ReconnectingWebSocket.
 *
 * Why a dumb transport: the robustness of SIP.js's recovery comes from two
 * guarantees this class provides, which the old AutoReconnectingWebSocket did
 * not:
 *   1. Every connect() attempt is GUARANTEED to settle (resolve on open, or
 *      reject on close/error/timeout) within `connectionTimeoutMs`. A zombie
 *      socket stuck in CONNECTING after a sleep can never stall the caller.
 *   2. A stale-socket identity guard (`ws !== this._ws`) on every handler makes
 *      events from an abandoned socket inert the instant a new socket is
 *      created — no boolean bookkeeping required.
 *
 * Parity reference: onsip/SIP.js @922920b2 transport.ts. Differences are
 * limited to: connect() takes a `url` argument (so the policy layer can supply
 * a freshly-resolved, token-bearing URL per attempt) and we drop the CRLF
 * keep-alive (our event protocol uses an application-level JSON ping handled by
 * the policy layer / SharedSocketService).
 */

import { stringifyForLog } from './log-utils';

export enum TransportState {
  Disconnected = 'Disconnected',
  Connecting = 'Connecting',
  Connected = 'Connected',
  Disconnecting = 'Disconnecting',
}

export interface WebSocketTransportOptions {
  /**
   * Time to wait for the socket to fire "open" before aborting the attempt and
   * rejecting connect(). Mirrors SIP.js `connectionTimeout` (default 5s).
   */
  connectionTimeoutMs?: number;
  /**
   * Extra headers to send on the websocket handshake (e.g. User-Agent). Only
   * honored in Node/Electron-main, where undici's WebSocket accepts a
   * non-standard `{ headers }` ctor argument. Browsers cannot set handshake
   * headers — leave unset there.
   */
  handshakeHeaders?: Record<string, string>;
}

export class WebSocketTransport {
  // Notified when the socket transitions INTO Connected.
  public onConnect?: () => void;
  // Notified when the socket transitions OUT of Connected. `error` is set only
  // for an unexpected close (network/server drop); it is undefined for a close
  // that resulted from an intentional disconnect(). The policy layer uses this
  // distinction to decide whether to reconnect.
  public onDisconnect?: (error?: Error) => void;
  // Notified for each inbound text message (raw string; parsing is the
  // consumer's concern).
  public onMessage?: (data: string) => void;
  // Notified on the underlying socket "error" event (always followed by close).
  public onError?: (event: Event) => void;

  private _state: TransportState = TransportState.Disconnected;
  private _ws?: WebSocket;
  private readonly connectionTimeoutMs: number;
  private readonly handshakeHeaders?: Record<string, string>;

  private connectPromise?: Promise<void>;
  private connectResolve?: () => void;
  private connectReject?: (error: Error) => void;
  private connectTimeoutId?: ReturnType<typeof setTimeout>;

  private disconnectPromise?: Promise<void>;
  private disconnectResolve?: () => void;
  private disconnectReject?: (error?: Error) => void;

  private transitioningState = false;

  private readonly LOG_PREFIX = '[WebSocketTransport]';

  constructor(options: WebSocketTransportOptions = {}) {
    this.connectionTimeoutMs = options.connectionTimeoutMs ?? 5000;
    this.handshakeHeaders = options.handshakeHeaders;
  }

  public get state(): TransportState {
    return this._state;
  }

  public isConnected(): boolean {
    return this._state === TransportState.Connected;
  }

  /**
   * Connect to `url`. Resolves once connected, otherwise rejects.
   * Always settles within `connectionTimeoutMs`.
   */
  public connect(url: string): Promise<void> {
    switch (this._state) {
      case TransportState.Connecting:
        // Already connecting — return the in-flight promise.
        return this.connectPromise ?? Promise.reject(new Error('Connecting without a connect promise'));
      case TransportState.Connected:
        return Promise.resolve();
      case TransportState.Disconnecting:
      case TransportState.Disconnected:
        this.transitionState(TransportState.Connecting);
        break;
      default:
        return Promise.reject(new Error('Unknown transport state'));
    }

    let ws: WebSocket;
    try {
      // undici (Node/Electron-main) accepts a non-standard `{ headers }` second
      // ctor argument; lib.dom does not, hence the narrow local cast. Browsers
      // never set handshakeHeaders, so they always take the 1-arg path.
      type NodeWebSocketCtor = new (url: string, init: { headers: Record<string, string> }) => WebSocket;
      ws = this.handshakeHeaders
        ? new (WebSocket as unknown as NodeWebSocketCtor)(url, { headers: this.handshakeHeaders })
        : new WebSocket(url);
      // Bind handlers to THIS specific socket. The identity guard inside each
      // handler ignores events once `this._ws` points elsewhere.
      ws.addEventListener('open', (ev: Event) => this.onWsOpen(ev, ws));
      ws.addEventListener('close', (ev: CloseEvent) => this.onWsClose(ev, ws));
      ws.addEventListener('error', (ev: Event) => this.onWsError(ev, ws));
      ws.addEventListener('message', (ev: MessageEvent) => this.onWsMessage(ev, ws));
      this._ws = ws;
    } catch (error) {
      this._ws = undefined;
      this.logError('WebSocket construction failed', { error: String(error) });
      return new Promise<void>((resolve, reject) => {
        this.connectResolve = resolve;
        this.connectReject = reject;
        this.transitionState(TransportState.Disconnected, error as Error);
      });
    }

    this.connectPromise = new Promise<void>((resolve, reject) => {
      this.connectResolve = resolve;
      this.connectReject = reject;
      this.connectTimeoutId = setTimeout(() => {
        this.logWarn('Connect timed out', { connectionTimeoutMs: this.connectionTimeoutMs });
        // Use the local `ws` reference, not this._ws. Closing triggers the
        // close handler, which transitions to Disconnected and rejects.
        try {
          ws.close(1000);
        } catch {
          // ignore
        }
      }, this.connectionTimeoutMs);
    });

    return this.connectPromise;
  }

  /**
   * Disconnect from the network. Resolves once disconnected.
   * A close that follows disconnect() is "clean" (no error passed to
   * onDisconnect), which tells the policy layer NOT to auto-reconnect.
   */
  public disconnect(): Promise<void> {
    switch (this._state) {
      case TransportState.Connecting:
      case TransportState.Connected:
        this.transitionState(TransportState.Disconnecting);
        break;
      case TransportState.Disconnecting:
        return this.disconnectPromise ?? Promise.reject(new Error('Disconnecting without a disconnect promise'));
      case TransportState.Disconnected:
        return Promise.resolve();
      default:
        return Promise.reject(new Error('Unknown transport state'));
    }

    const ws = this._ws;
    if (!ws) {
      // No socket to close (e.g. construction failed); settle immediately.
      this.transitionState(TransportState.Disconnected);
      return Promise.resolve();
    }

    // Capture the promise in a local. Assigning this.disconnectPromise before
    // ws.close() lets onWsClose recognize a (possibly synchronous) close as
    // intentional, but transitionState() clears this.disconnectPromise as part
    // of completing the disconnect — so we return the local reference, which a
    // synchronous close cannot null out from under us.
    const disconnectPromise = new Promise<void>((resolve, reject) => {
      this.disconnectResolve = resolve;
      this.disconnectReject = reject;
    });
    this.disconnectPromise = disconnectPromise;

    try {
      ws.close(1000);
    } catch (error) {
      this.logError('WebSocket close failed', { error: String(error) });
    }

    return disconnectPromise;
  }

  /**
   * Send a text message. No-op (with a warning) if not connected — the higher
   * layer is responsible for caching/queueing while disconnected.
   */
  public send(message: string): void {
    if (this._state !== TransportState.Connected || !this._ws) {
      this.logWarn('Cannot send - not connected', { state: this._state });
      return;
    }
    try {
      this._ws.send(message);
    } catch (error) {
      this.logError('WebSocket send failed', { error: String(error) });
    }
  }

  // ========== Native socket event handlers (all identity-guarded) ==========

  private onWsOpen(_ev: Event, ws: WebSocket): void {
    if (ws !== this._ws) {
      return;
    }
    if (this._state === TransportState.Connecting) {
      this.transitionState(TransportState.Connected);
    }
  }

  private onWsClose(ev: CloseEvent, ws: WebSocket): void {
    if (ws !== this._ws) {
      return;
    }
    // If there is no in-flight disconnectPromise, this close was unexpected.
    const error = !this.disconnectPromise ? new Error(`WebSocket closed unexpectedly (code: ${ev.code})`) : undefined;
    if (error) {
      this.logWarn('WebSocket closed unexpectedly', { code: ev.code, reason: ev.reason });
    } else {
      this.log('WebSocket closed', { code: ev.code, reason: ev.reason });
    }
    // Clear the socket before transitioning so the consumer sees a clean state.
    this._ws = undefined;
    this.transitionState(TransportState.Disconnected, error);
  }

  private onWsError(ev: Event, ws: WebSocket): void {
    if (ws !== this._ws) {
      return;
    }
    this.logError('WebSocket error');
    this.onError?.(ev);
  }

  private onWsMessage(ev: MessageEvent, ws: WebSocket): void {
    if (ws !== this._ws) {
      return;
    }
    if (this._state !== TransportState.Connected) {
      return;
    }
    if (typeof ev.data !== 'string') {
      // Our event protocol is text JSON; ignore binary frames.
      return;
    }
    this.onMessage?.(ev.data);
  }

  // ========== State machine ==========

  /**
   * Transition transport state, validating the transition, settling any
   * outstanding connect/disconnect promises, and firing onConnect/onDisconnect.
   * Faithful port of SIP.js Transport.transitionState.
   */
  private transitionState(newState: TransportState, error?: Error): void {
    if (this.transitioningState) {
      // A re-entrant transition (e.g. a callback synchronously calling
      // connect/disconnect) would corrupt the machine. Guard against it.
      this.logError('State transition loop detected', { from: this._state, to: newState });
      return;
    }
    this.transitioningState = true;

    const invalid = (): void => {
      this.logError('Invalid state transition', { from: this._state, to: newState });
    };

    switch (this._state) {
      case TransportState.Connecting:
        if (
          newState !== TransportState.Connected &&
          newState !== TransportState.Disconnecting &&
          newState !== TransportState.Disconnected
        ) {
          invalid();
        }
        break;
      case TransportState.Connected:
        if (newState !== TransportState.Disconnecting && newState !== TransportState.Disconnected) {
          invalid();
        }
        break;
      case TransportState.Disconnecting:
        if (newState !== TransportState.Connecting && newState !== TransportState.Disconnected) {
          invalid();
        }
        break;
      case TransportState.Disconnected:
        if (newState !== TransportState.Connecting) {
          invalid();
        }
        break;
    }

    const oldState = this._state;
    this._state = newState;

    // Snapshot promise callbacks before clearing (a fired callback could
    // indirectly mutate them).
    const connectResolve = this.connectResolve;
    const connectReject = this.connectReject;
    if (oldState === TransportState.Connecting) {
      this.connectPromise = undefined;
      this.connectResolve = undefined;
      this.connectReject = undefined;
    }

    const disconnectResolve = this.disconnectResolve;
    const disconnectReject = this.disconnectReject;
    if (oldState === TransportState.Disconnecting) {
      this.disconnectPromise = undefined;
      this.disconnectResolve = undefined;
      this.disconnectReject = undefined;
    }

    // A settled connect attempt no longer needs its timeout.
    if (this.connectTimeoutId) {
      clearTimeout(this.connectTimeoutId);
      this.connectTimeoutId = undefined;
    }

    this.log('Transitioned', { from: oldState, to: newState });

    // Fire lifecycle callbacks.
    if (newState === TransportState.Connected) {
      this.onConnect?.();
    }
    if (oldState === TransportState.Connected) {
      this.onDisconnect?.(error);
    }

    // Settle the connect promise.
    if (oldState === TransportState.Connecting) {
      if (newState === TransportState.Connected) {
        connectResolve?.();
      } else {
        connectReject?.(error ?? new Error('Connect aborted'));
      }
    }

    // Settle the disconnect promise.
    if (oldState === TransportState.Disconnecting) {
      if (newState === TransportState.Disconnected) {
        disconnectResolve?.();
      } else {
        disconnectReject?.(error ?? new Error('Disconnect aborted'));
      }
    }

    this.transitioningState = false;
  }

  // ========== Logging ==========

  private log(message: string, data?: Record<string, unknown>): void {
    if (data) {
      console.log(`${this.LOG_PREFIX} ${message}`, stringifyForLog(data));
    } else {
      console.log(`${this.LOG_PREFIX} ${message}`);
    }
  }

  private logWarn(message: string, data?: Record<string, unknown>): void {
    if (data) {
      console.warn(`${this.LOG_PREFIX} ${message}`, stringifyForLog(data));
    } else {
      console.warn(`${this.LOG_PREFIX} ${message}`);
    }
  }

  private logError(message: string, data?: Record<string, unknown>): void {
    if (data) {
      console.error(`${this.LOG_PREFIX} ${message}`, stringifyForLog(data));
    } else {
      console.error(`${this.LOG_PREFIX} ${message}`);
    }
  }
}
